// The station window's Games and apps tab (F-CAT-03, F-CAT-09, ADR-0056; spec/13 §3.3): one summary line ("11 of 12 installed ·
// checked today 1:23 PM") with Rescan on its right, filter chips (All · Updating · Needs attention), and a full-width list of the
// library titles the PC's class is offered — a name with its version small and muted, and one status pill. An update shows its
// percent while the PC is online and "paused at N%" while it is not; an app with a recipe that is missing offers Install (and a
// failed install Retry) with `library:manage`. The launchers the PC found close the list. The dialog scrolls, not the list.

import type { FloorStationState, StationLibraryTitleState } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Chip, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { InstallAppButton } from '../org/library/InstallAppButton';
import { useRescanInstalls } from '../org/library/queries';
import { installActionOf } from '../org/library/players';
import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { useStationInstalled } from './queries';
import { appStatusOf, filterCounts, inFilter, type AppFilter, type AppStatus } from './station-apps';
import { useProblem } from '../api/use-problem';

/** Launcher codes are catalogue data (the launchers' own names), not copy. */
const LAUNCHER_NAMES: Readonly<Record<string, string>> = {
  steam: 'Steam',
  epic: 'Epic Games',
  riot: 'Riot Client',
  ea: 'EA app',
  ubisoft: 'Ubisoft Connect',
  battlenet: 'Battle.net',
  microsoft_store: 'Microsoft Store',
};

const FILTERS: readonly AppFilter[] = ['all', 'updating', 'attention'];

export function StationApps({
  station,
  online,
  canRescan,
  canInstall,
  now,
}: {
  readonly station: FloorStationState;
  /** Whether the PC holds a connection now: an update on an offline PC reads "paused", not live. */
  readonly online: boolean;
  readonly canRescan: boolean;
  /** `library:manage`: the rows offer Install / Retry (ADR-0056). */
  readonly canInstall: boolean;
  /** The clock the window counts against, for "checked today". */
  readonly now: number;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const formatter = useVenueFormatter();
  const installed = useStationInstalled(venue.organizationId, venue.id, station.stationId, true);
  const rescan = useRescanInstalls(venue.organizationId, venue.id);
  const [filter, setFilter] = useState<AppFilter>('all');
  const data = installed.data;

  // `station.library.rescan` (F-CAT-03): the station scans now and reports; the refetch shows the new scan time.
  const rescanNow = () =>
    rescan.mutate(
      { stationIds: [station.stationId] },
      {
        onSuccess: () => show({ tone: 'ok', title: t('floor.drawer.installed.rescanSent') }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('floor.drawer.installed.rescanFailed') }) }),
      },
    );

  if (installed.isError) return <p className="console-muted">{problem.text(installed.error, { fallback: t('floor.drawer.installed.failed') })}</p>;
  if (data === undefined) return <p className="console-muted">{t('floor.drawer.installed.loading')}</p>;
  if (data.titles.length === 0) return <p className="console-muted">{t('floor.drawer.installed.noLibrary')}</p>;

  const rows = data.titles.map((title) => ({ title, status: appStatusOf(title, online) }));
  const counts = filterCounts(rows.map((row) => row.status));
  const shown = rows.filter((row) => inFilter(row.status, filter));
  const scannedAt = data.scannedAt;
  const checked =
    scannedAt === null
      ? t('floor.drawer.installed.neverReported')
      : formatter.date(scannedAt, 'short') === formatter.date(now, 'short')
        ? t('floor.drawer.installed.checkedToday', { time: formatter.time(scannedAt, 'short') })
        : t('floor.drawer.installed.checkedOn', { when: formatter.dateTime(scannedAt, { date: 'medium', time: 'short' }) });

  return (
    <div className="console-station-apps">
      <div className="console-station-apps__summary">
        <p className="console-station-apps__line">
          <strong className="console-num">{t('floor.drawer.installed.summary', { n: data.installedCount, total: data.titles.length })}</strong>
          <span className="console-muted">{` · ${checked}`}</span>
        </p>
        {canRescan ? (
          <Button variant="ghost" size="sm" icon={<Icon name="refresh" />} busy={rescan.isPending} onClick={rescanNow}>
            {t('floor.drawer.installed.rescan')}
          </Button>
        ) : null}
      </div>

      <div className="console-station-apps__chips" role="group" aria-label={t('floor.drawer.installed.filter')}>
        {FILTERS.map((value) => (
          <Chip key={value} selected={filter === value} onSelectedChange={() => setFilter(value)} count={counts[value]}>
            {t(`floor.drawer.installed.filters.${value}`)}
          </Chip>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="console-muted">{t('floor.drawer.installed.filterEmpty')}</p>
      ) : (
        <ul className="console-station-apps__list" aria-label={t('floor.drawer.installed.title')}>
          {shown.map(({ title, status }) => (
            <AppRow key={title.entryId} station={station} title={title} status={status} canInstall={canInstall} />
          ))}
        </ul>
      )}

      {data.launchers.length > 0 ? (
        <p className="console-muted console-station-apps__launchers">
          {t('floor.drawer.installed.launchers', { launchers: data.launchers.map((code) => LAUNCHER_NAMES[code] ?? code).join(', ') })}
        </p>
      ) : null}
    </div>
  );
}

/** The pill's words for a status: "Updating 64%", "Update paused at 64%", "Installed"… (shared with the library's per-PC list). */
export function statusWords(status: Pick<AppStatus, 'key' | 'percent'>, t: (key: string, options?: Record<string, unknown>) => string): string {
  return t(`library.players.pill.${status.key}`, { percent: status.percent });
}

function AppRow({
  station,
  title,
  status,
  canInstall,
}: {
  readonly station: FloorStationState;
  readonly title: StationLibraryTitleState;
  readonly status: AppStatus;
  readonly canInstall: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const formatter = useVenueFormatter();
  const action = canInstall ? installActionOf(title) : null;
  const provisioning = title.provisioning;
  // Why it is not simply installed: what the PC said about it for players, in one muted sentence (F-CAT-09).
  const why =
    provisioning === null || provisioning.state === 'installed' || provisioning.state === 'queued' || provisioning.state === 'installing'
      ? null
      : [
          t(`library.players.state.${provisioning.state}`),
          provisioning.failure === null ? null : t(`library.players.failure.${provisioning.failure}`),
          provisioning.detail === null ? null : t('library.players.detail', { detail: provisioning.detail }),
          provisioning.nextAttemptAt === null ? null : t('library.players.retry', { time: formatter.dateTime(provisioning.nextAttemptAt, { date: 'medium', time: 'short' }) }),
        ]
          .filter((part): part is string => part !== null)
          .join(' ');
  const hint = status.key === 'unverified' ? t('floor.drawer.installed.unverifiedHint') : (why ?? undefined);

  return (
    <li className="console-station-apps__row">
      <span className="console-station-apps__name">
        <span className="console-station-apps__title">{title.name}</span>
        {title.version === null ? null : <span className="console-muted console-station-apps__version">{t('floor.drawer.installed.version', { version: title.version })}</span>}
      </span>
      <span className="console-station-apps__end">
        <span title={hint}>
          <Badge tone={status.tone}>{statusWords(status, t)}</Badge>
        </span>
        {action === null ? null : <InstallAppButton stationId={station.stationId} entryId={title.entryId} name={title.name} action={action} />}
      </span>
      {why === null ? null : <p className={`console-station-apps__why${status.key === 'failed' ? ' is-danger' : ''}`}>{why}</p>}
    </li>
  );
}
